import { useState, useRef } from 'react'
import { Button } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Upload, FileText, X } from 'lucide-react'

interface FileUploadProps {
  onFileLoad: (content: string, fileName: string) => void
  fileName?: string
  fileContent?: string
}

export function FileUpload({ onFileLoad, fileName, fileContent }: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const handleFileSelect = async (file: File) => {
    setError('')
    
    // ファイルサイズチェック（10MB制限）
    if (file.size > 10 * 1024 * 1024) {
      setError('ファイルサイズは10MB以下にしてください')
      return
    }
    
    // ファイルタイプチェック
    const validTypes = ['text/plain', 'text/markdown']
    const validExtensions = ['.txt', '.md']
    
    const hasValidType = validTypes.includes(file.type)
    const hasValidExtension = validExtensions.some(ext => file.name.endsWith(ext))
    
    if (!hasValidType && !hasValidExtension) {
      setError('対応していないファイル形式です（.txt, .mdのみ対応）')
      return
    }
    
    // テキストファイル読み込み
    const reader = new FileReader()
    reader.onload = (event) => {
      const content = event.target?.result as string
      onFileLoad(content, file.name)
    }
    reader.onerror = () => {
      setError('ファイルの読み込みに失敗しました')
    }
    
    reader.readAsText(file, 'UTF-8')
  }
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    
    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }
  
  const handleClear = () => {
    onFileLoad('', '')
    setError('')
  }
  
  return (
    <div className="space-y-4">
      <Label>ファイルアップロード</Label>
      
      {fileName && fileContent ? (
        <div className="border rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">{fileName}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {fileContent.length.toLocaleString()}文字
          </p>
          <div className="mt-2 p-2 bg-muted rounded text-xs font-mono max-h-[100px] overflow-auto">
            {fileContent.slice(0, 500)}
            {fileContent.length > 500 && '...'}
          </div>
        </div>
      ) : (
        <div
          className={`
            border-2 border-dashed rounded-lg p-8 text-center
            transition-colors cursor-pointer
            ${isDragging 
              ? 'border-primary bg-primary/10' 
              : 'border-muted-foreground/25 hover:border-muted-foreground/50'
            }
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) {
                handleFileSelect(file)
              }
            }}
            className="hidden"
          />
          
          <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
          <p className="font-medium mb-2">
            ファイルをドラッグ&ドロップ
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            または
          </p>
          <Button variant="outline" size="sm">
            ファイルを選択
          </Button>
          <p className="text-xs text-muted-foreground mt-4">
            対応形式: .txt, .md (最大10MB)
          </p>
        </div>
      )}
      
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}